import { Accessor, DefaultProps } from '@deck.gl/core';
import { IconLayer, IconLayerProps } from '@deck.gl/layers';
import hiddable from '../modules/isHiddableModule.js';
import fragmentShader from './flight-icon-fragment-shader.glsl?raw';
import { FlightIconLayerUniforms, flightIconUniforms } from './flight-icon-layer-uniforms.js';
import vertexShader from './flight-icon-vertex-shader.glsl?raw';

type _FlightIconLayerProps<TData> = {
    getIsHidden: Accessor<TData, number>;
    outlineColor: [number, number, number, number];
    shadowColor: [number, number, number, number];
    showShadow: boolean;
    isGlobeView: boolean;
    sdfParams: {
        cutoff: number;
        softEnabled: boolean;
        softEdgeMin: number;
        softEdgeMax: number;
        outlineEnabled: boolean;
        outlineMin0: number;
        outlineMin1: number;
        outlineMax0: number;
        outlineMax1: number;
    };
};

export type FlightIconLayerProps<TData> = _FlightIconLayerProps<TData> & IconLayerProps<TData>;

const defaultProps: DefaultProps<FlightIconLayerProps<any> & IconLayerProps> = {
    ...IconLayer.defaultProps,
    billboard: false,
    sizeUnits: 'pixels',
    outlineColor: { type: 'color', value: [0, 0, 0, 0.5] },
    shadowColor: { type: 'color', value: [0, 0, 0, 0.2] },
    showShadow: false,
    isGlobeView: false,
    parameters: {
        cullMode: 'front',
        blend: true,
        blendColorSrcFactor: 'one',
        blendColorDstFactor: 'one-minus-src-alpha',
        blendColorOperation: 'add',
        blendAlphaSrcFactor: 'one',
        blendAlphaDstFactor: 'one-minus-src-alpha',
        blendAlphaOperation: 'add',
        // Need to disable depth test because during pitch movements or in globe view we're having a lot of z-fighting
        depthWriteEnabled: false,
        depthCompare: 'always'
    }
};

export class FlightsIconLayer<TData = unknown, TProps = object> extends IconLayer<TData, FlightIconLayerProps<TData> & TProps> {
    static defaultProps: any = defaultProps;
    initializeState() {
        super.initializeState();
        this.getAttributeManager()!.addInstanced({
            isHidden: {
                size: 1,
                accessor: 'getIsHidden',
                defaultValue: 0.0
            }
        });
    }

    getShaders() {
        const superShaders = super.getShaders();
        return {
            ...superShaders,
            modules: [...superShaders.modules, hiddable, flightIconUniforms],
            vs: vertexShader,
            fs: fragmentShader
        };
    }

    draw(params: any) {
        const { model } = this.state;

        const uniforms: FlightIconLayerUniforms = {
            isGlobeView: this.props.isGlobeView,
            currentZoom: this.context.viewport.zoom,

            cutoff: this.props.sdfParams.cutoff,

            softEdge: this.props.sdfParams.softEnabled,
            softEdgeMin: this.props.sdfParams.softEdgeMin,
            softEdgeMax: this.props.sdfParams.softEdgeMax,

            outline: this.props.sdfParams.outlineEnabled,
            outlineColor: this.props.outlineColor,
            outlineMin0: this.props.sdfParams.outlineMin0,
            outlineMin1: this.props.sdfParams.outlineMin1,
            outlineMax0: this.props.sdfParams.outlineMax0,
            outlineMax1: this.props.sdfParams.outlineMax1,

            shadow: this.props.showShadow,
            shadowColor: this.props.shadowColor
        };

        model?.shaderInputs.setProps({ feFlightIcon: uniforms });
        super.draw(params);
    }
}
